import { getStatusClass, getTranslate } from "../../helpers/Offer";
import React from "react";
import { CopyIcon, EditIcon } from "lucide-react";
import { stringSlip } from "../common/RenderFunctions";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { GridPaginationState } from "@mui/x-data-grid/hooks/features/pagination/gridPaginationInterfaces";
import OfferProposal from "../../model/OfferProposal";

interface OffersFilterParams {
  search: string;
  reseller_id?: string;
  commercial_id?: string;
  page: number;
  per_page: number;
  sort_by: string;
  sort_direction: "asc" | "desc";
  begin_date?: string;
  end_date?: string;
  status?: string;
  type?: string;
}

interface IProps {
  offers: OfferProposal[];
  filters: OffersFilterParams;
  setFilters: (filters: OffersFilterParams) => void;
  totalPages: number;
  totalResults?: number;
  loading: boolean;
  resellersList: ResellerSummary[];
  commercialsList: ResellerSummary[];
  handleManageOffer: (offer: OfferProposal) => void;
  handleCopyOffer: (offer: OfferProposal) => void;
  userSettings: ProfileAndSettings | null;
}

const ListOfferItems: React.FC<IProps> = ({
  offers,
  filters,
  setFilters,
  totalPages,
  resellersList,
  commercialsList,
  handleManageOffer,
  handleCopyOffer,
  userSettings,
  totalResults,
  loading,
}: IProps) => {
  const [showListedFields, setShowListedFields] = React.useState<
    Map<string, boolean>
  >(() => {
    const session = sessionStorage.getItem("offers_listed_fields");
    if (session) {
      return new Map<string, boolean>(JSON.parse(session));
    }
    return new Map<string, boolean>([
      ["offer_type", true],
      ["external_id", true],
      ["item", true],
      ["value", true],
      ["client_name", true],
      ["created_by", true],
      ["created_by_role", true],
      ["status", true],
      ["reseller_cod", true],
      ["reseller", true],
      ["commercial_portfolio", true],
      ["commercial_agent", true],
      ["created_at", true],
      ["expires_on", true],
      ["actions", true],
    ]);
  });

  // Save showListedFields to sessionStorage when it changes
  React.useEffect(() => {
    sessionStorage.setItem(
      "offers_listed_fields",
      JSON.stringify(Array.from(showListedFields.entries())),
    );
  }, [showListedFields]);

  const isAdmin = userSettings?.isAdmin() || false;
  const isCommercial = userSettings?.isCommercial() || false;
  const isAdminOrCommercial = isAdmin || isCommercial;

  const getOrderTypeLabel = (offer: OfferProposal): string => {
    if (offer.offer_type === "DEFAULT") {
      return "Revendedor";
    } else if (offer.offer_type === "INTERNAL") {
      if (offer.client_type.toUpperCase() === "RESELLER") {
        return "Comercial";
      } else if (offer.client_type.toUpperCase() === "END_CUSTOMER") {
        return "Interna";
      }
    }
    return "Outro";
  };

  const getOrderTypeClass = (offer: OfferProposal): string => {
    if (offer.offer_type === "DEFAULT") {
      return "bg-amber-300";
    } else if (offer.offer_type === "INTERNAL") {
      if (offer.client_type.toUpperCase() === "RESELLER") {
        return "bg-blue-300";
      } else if (offer.client_type.toUpperCase() === "END_CUSTOMER") {
        return "bg-green-300";
      }
    }
    return "bg-gray-300";
  };

  const getStyleCssForSellerRole = (
    role: "ADMIN" | "COMMERCIAL" | "RESELLER" | "USER" | "UNKNOWN",
  ) => {
    switch (role) {
      case "ADMIN":
        return "bg-red-100 text-red-800";
      case "COMMERCIAL":
        return "bg-blue-100 text-blue-800";
      case "RESELLER":
        return "bg-green-100 text-green-800";
      case "USER":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getDisplayValueForSellerRole = (
    role: "ADMIN" | "COMMERCIAL" | "RESELLER" | "USER" | "UNKNOWN",
  ) => {
    switch (role) {
      case "ADMIN":
        return "Administrador";
      case "COMMERCIAL":
        return "Comercial";
      case "RESELLER":
        return "Revendedor";
      case "USER":
        return "Self-Service";
      default:
        return "Desconhecido";
    }
  };

  const handlePerPageChange = (perPage: number) => {
    setFilters({
      ...filters,
      per_page: perPage,
      page: 1, // Reset to first page on perPage change
    });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({
      ...filters,
      page: newPage,
    });
  };

  const handleFilterValueChange = (field: string) => {
    return (value: string) =>
      setFilters({
        ...filters,
        [field]: value,
        page: 1, // Reset to first page on filter change
      });
  };

  const renderOrigemSelect = () => {
    return (
      <div className="control flex-1 flex items-center gap-2 pl-10">
        <label className="label mb-0 whitespace-nowrap">Origem</label>
        <div className="select w-full">
          <select
            value={filters.type}
            onChange={(e) => handleFilterValueChange("type")(e.target.value)}
            className="border rounded px-2 py-1 bg-white"
            style={{ width: "100%" }}
          >
            <option value="">Escolha a origem</option>
            <option value="DEFAULT">Revendedor</option>
            <option value="RESELLER">Comercial</option>
            <option value="END_CUSTOMER">Interna</option>
          </select>
        </div>
      </div>
    );
  };

  const renderCommercialSelect = () => {
    return (
      <div className="control flex-1 flex items-center gap-2 pl-10">
        <label className="label mb-0 whitespace-nowrap">Cod Comercial</label>
        <div className="select w-full">
          <select
            name="reseller"
            onChange={(e) => {
              handleFilterValueChange("commercial_id")(e.target.value);
            }}
            className="w-full"
            value={filters.commercial_id || ""}
          >
            <option value="">Todos</option>
            {commercialsList.map((commercial) => (
              <option key={commercial.id} value={commercial.id}>
                {commercial.description || "Sem comercial"}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderResellerSelect = () => {
    return (
      <div className="control flex-1 flex items-center gap-2 pl-10">
        <label className="label mb-0 whitespace-nowrap">Revendedor</label>
        <div className="select w-full">
          <select
            name="reseller"
            onChange={(e) => {
              handleFilterValueChange("reseller_id")(e.target.value);
            }}
            className="w-full"
            value={filters.reseller_id || ""}
          >
            <option value="">Todos</option>
            {resellersList.map((reseller) => (
              <option key={reseller.id} value={reseller.id}>
                {reseller.description || "revendedor sem nome"}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderFilterForm = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-8 gap-8">
        {/* Search Input */}
        <div className="md:col-span-5">
          <label
            htmlFor="search"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Pesquisar
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              id="search"
              type="text"
              name="search"
              placeholder="ID, computador, cliente..."
              value={filters.search}
              onChange={(e) =>
                handleFilterValueChange("search")(e.target.value)
              }
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
              autoFocus={true}
            />
          </div>
        </div>

        {/* Date Range */}
        <div>
          <label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Data de
          </label>
          <input
            id="startDate"
            type="date"
            name="startDate"
            className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
            value={
              filters.begin_date ||
              new Date(Date.now() - 8 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0]
            }
            onChange={(e) =>
              handleFilterValueChange("begin_date")(e.target.value)
            }
          />
        </div>

        <div>
          <label
            htmlFor="endDate"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            até
          </label>
          <input
            id="endDate"
            type="date"
            name="endDate"
            className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
            value={filters.end_date || new Date().toISOString().split("T")[0]}
            onChange={(e) =>
              handleFilterValueChange("end_date")(e.target.value)
            }
          />
        </div>

        <div>
          <label
            htmlFor="status"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Estado
          </label>
          <select
            id="status"
            name="status"
            className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
            value={filters.status}
            onChange={(e) => handleFilterValueChange("status")(e.target.value)}
          >
            {[
              "Todos",
              "REQUESTED",
              "DRAFT",
              "SENT",
              "ACCEPTED",
              "CANCELLED",
              "EXPIRED",
              "PENDING",
              "CONFIRMED",
              "REJECTED",
              "SHIPPED",
              "COMPLETED",
              "ARCHIVED",
            ].map((status) => (
              <option key={status} value={status === "Todos" ? "" : status}>
                {status === "Todos" ? "Todos" : getTranslate(status)}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderAdminFilterForm = () => {
    return (
      isAdminOrCommercial && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          {/* Origin Select */}
          <div>
            <label
              htmlFor="origin"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Origem
            </label>
            <select
              id="origin"
              value={filters.type}
              onChange={(e) => handleFilterValueChange("type")(e.target.value)}
              className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
            >
              <option value="">Todas as origens</option>
              <option value="DEFAULT">Revendedor</option>
              <option value="RESELLER">Comercial</option>
              <option value="END_CUSTOMER">Interna</option>
            </select>
          </div>

          {/* Reseller Select */}
          <div>
            <label
              htmlFor="reseller"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Revendedor
            </label>
            <select
              id="reseller"
              name="reseller"
              onChange={(e) => {
                handleFilterValueChange("reseller_id")(e.target.value);
              }}
              className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
              value={filters.reseller_id || ""}
            >
              <option value="">Todos</option>
              {resellersList.map((reseller) => (
                <option key={reseller.id} value={reseller.id}>
                  {reseller.description || "revendedor sem nome"}
                </option>
              ))}
            </select>
          </div>

          {/* Commercial Select (Admin only) */}
          {isAdmin && (
            <div>
              <label
                htmlFor="commercial"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Cod Comercial
              </label>
              <select
                id="commercial"
                name="commercial"
                onChange={(e) => {
                  handleFilterValueChange("commercial_id")(e.target.value);
                }}
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
                value={filters.commercial_id || ""}
              >
                <option value="">Todos</option>
                {commercialsList.map((commercial) => (
                  <option key={commercial.id} value={commercial.id}>
                    {commercial.description || "Sem comercial"}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )
    );
  };

  const renderOfferType = (offer: OfferProposal) => {
    return (
      <small
        className={`rounded-3xl px-2 p-1 has-text-weight-bold ${getOrderTypeClass(offer)}`}
      >
        {" "}
        {getOrderTypeLabel(offer)}{" "}
      </small>
    );
  };

  const renderValue = (offer: OfferProposal) => {
    return offer.compound.price.value.toLocaleString("pt-PT", {
      style: "currency",
      currency: "EUR",
    });
  };

  const renderCreatedRole = (offer: OfferProposal) => {
    const value = offer.created_by_role as
      | "ADMIN"
      | "COMMERCIAL"
      | "RESELLER"
      | "USER"
      | "UNKNOWN";
    return (
      <small
        className={`rounded-3xl px-2 p-1 has-text-weight-bold ${getStyleCssForSellerRole(value)}`}
      >
        {" "}
        {getDisplayValueForSellerRole(value)}{" "}
      </small>
    );
  };

  const renderCommercialAgents = (offer: OfferProposal) => {
    return offer.commercial_agents.length > 0 ? (
      offer.commercial_agents.map((agent) => (
        <span
          key={agent.id}
          className="inline-block mr-1 rounded bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800"
        >
          {agent.name || agent.username}
        </span>
      ))
    ) : (
      <small>-</small>
    );
  };

  const renderActions = (offer: OfferProposal) => {
    return (
      <div>
        <button
          className="button is-small is-primary"
          onClick={(e) => {
            e.stopPropagation();
            handleManageOffer(offer);
          }}
        >
          <EditIcon className="w-4 h-4" />
        </button>
        <button
          className="button is-small is-secondary ml-2"
          onClick={(e) => {
            e.stopPropagation();
            handleCopyOffer(offer);
          }}
        >
          <CopyIcon className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "offer_type",
      headerName: "Origem",
      width: 120,
      sortable: true,
      renderCell: (params) => {
        return renderOfferType(params.row as OfferProposal);
      },
    },
    { field: "external_id", headerName: "Proposta #", width: 200 },
    {
      field: "item",
      headerName: "Computador",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => stringSlip(row.compound?.id, 30),
    },
    {
      field: "value",
      headerName: "Valor",
      width: 100,
      sortable: true,
      renderCell: (params) => {
        return renderValue(params.row as OfferProposal);
      },
    },
    {
      field: "client_name",
      headerName: "Nome do Cliente",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => stringSlip(row.client?.name || "n/a", 30),
    },
    {
      field: "created_by",
      headerName: "Vendedor",
      width: 150,
      sortable: true,
      valueGetter: (value, row) =>
        stringSlip(row.created_by_name || row.created_by || "Desconhecido", 15),
    },
    {
      field: "created_role",
      headerName: "Perfil do Vendedor",
      width: 150,
      sortable: true,
      renderCell: (params) => {
        return renderCreatedRole(params.row as OfferProposal);
      },
    },
    {
      field: "status",
      headerName: "Estado",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => getTranslate(row.status),
    },
    ...(isAdminOrCommercial
      ? [
          {
            field: "reseller_cod",
            headerName: "Cod Revendedor",
            width: 100,
            sortable: true,
            valueGetter: (value, row) => row.reseller_id || "-",
          } as GridColDef,
          {
            field: "reseller",
            headerName: "Revendedor",
            width: 200,
            sortable: true,
            valueGetter: (value, row) =>
              stringSlip(row.reseller_name || "Revendedor desconhecido", 25),
          } as GridColDef,
          {
            field: "commercial_portfolio",
            headerName: "Cod Comercial",
            width: 120,
            sortable: true,
            valueGetter: (value, row) => row.commercial_portfolio || "-",
          } as GridColDef,
          {
            field: "commercial_agent",
            headerName: "Comercial",
            width: 200,
            sortable: true,
            renderCell: (params) => {
              return renderCommercialAgents(params.row as OfferProposal);
            },
          } as GridColDef,
        ]
      : []),
    {
      field: "created_at",
      headerName: "Criada em",
      width: 140,
      sortable: true,
      valueGetter: (value, row) =>
        row?.created_at
          ? new Date(row.created_at).toLocaleString("pt-PT", {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
    },
    {
      field: "expires_on",
      headerName: "Expira em",
      width: 140,
      sortable: true,
      valueGetter: (value, row) =>
        row?.expires_on
          ? new Date(row.expires_on).toLocaleString("pt-PT", {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
    },
    {
      field: "actions",
      headerName: "",
      width: 150,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return renderActions(params.row as OfferProposal);
      },
    },
  ];

  const paginationState: GridPaginationState = {
    paginationMode: "server",
    paginationModel: {
      page: filters.page - 1,
      pageSize: filters.per_page || 10,
    },
    rowCount: totalResults || -1,
    enabled: true,
    meta: {
      hasNextPage: true,
    },
  };

  const handlePaginationChange = (model: {
    page: number;
    pageSize: number;
  }) => {
    if (model.pageSize !== filters.per_page) {
      handlePerPageChange(model.pageSize);
    }
    if (model.page + 1 !== filters.page) {
      handlePageChange(model.page + 1);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          {renderFilterForm()}
          {renderAdminFilterForm()}
        </div>
      </div>
      <div className="manage-page mt-4">
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <DataGrid
            rows={offers}
            columns={columns}
            loading={loading}
            initialState={{ pagination: paginationState }}
            rowCount={totalResults || 0}
            paginationMode="server"
            disableColumnFilter={true}
            onPaginationModelChange={handlePaginationChange}
            onSortModelChange={(model) => {
              if (model.length > 0) {
                const sortModel = model[0];
                setFilters({
                  ...filters,
                  ["sort_direction"]: sortModel.sort || "asc",
                  ["sort_by"]: sortModel.field,
                  page: 1, // Reset to first page on filter change
                });
              }
            }}
            columnVisibilityModel={Object.fromEntries(showListedFields)}
            onColumnVisibilityModelChange={(newModel) => {
              const newMap = new Map<string, boolean>(Object.entries(newModel));
              setShowListedFields(newMap);
            }}
            hideFooterSelectedRowCount={true}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            localeText={{
              columnsManagementSearchTitle: "Procurar",
              columnsManagementNoColumns: "Sem colunas",
              columnsManagementShowHideAllText: "Mostrar/Ocultar Todas",
              columnsManagementReset: "Repor",
              columnsManagementDeleteIconLabel: "Limpar",
              columnMenuLabel: "Menu",
              columnMenuAriaLabel: (columnName: string) =>
                `Menu da coluna ${columnName}`,
              columnMenuShowColumns: "Mostrar colunas",
              columnMenuManageColumns: "Gerir colunas",
              columnMenuFilter: "Filtrar",
              columnMenuHideColumn: "Ocultar coluna",
              columnMenuUnsort: "Remover ordenação",
              columnMenuSortAsc: "Ordenar por ASC",
              columnMenuSortDesc: "Ordenar por DESC",
              columnMenuManagePivot: "Gerir pivot",
              paginationRowsPerPage: "Linhas por página:",
              paginationDisplayedRows: ({ from, to, count, estimated }) => {
                if (!estimated) {
                  return `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`;
                }
                const estimatedLabel =
                  estimated && estimated > to
                    ? `cerca de ${estimated}`
                    : `mais de ${to}`;
                return `${from}–${to} de ${count !== -1 ? count : estimatedLabel}`;
              },
            }}
            getCellClassName={(params) => {
              const fieldClasses: Record<string, string> = {
                external_id: "font-mono",
                value: "font-mono force-text-right",
                status: getStatusClass(params.row.status as string),
                offer_type: "force-text-center",
                created_at: "font-mono text-xs",
                expires_on: "font-mono text-xs",
              };
              return fieldClasses[params.field] || "";
            }}
            getRowId={(row) => row.id}
          />
        </Paper>
      </div>
    </>
  );
};

export default ListOfferItems;
